/************************************************************************************************************
Author                          : Trizent Technologies Private Limited
Date                            : 
Notes/Remarks                   : 

Organization:  	Trizent Technologies Private Limited.
		Module No. 17, SBC, Thejaswini Building
		Phase 1, Technopark, Thiruvananthapuram - 695581, 
		Kerala, India
 
Copyright 2026 (C) Trizent Technologies Pvt Ltd. (www.trizentinc.com)
All rights reserved.
***************************************************************************************************************/

import React from "react";

interface GptAuditSectionProps {
  gptTest: any;
  t: (key: string) => string;
}

const GptAuditSection: React.FC<GptAuditSectionProps> = ({ gptTest, t }) => {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-10">
          <div className="lg:col-span-2 bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
            <div className="px-4 py-3 border-b border-slate-100 bg-slate-50/30 flex items-center justify-between">
              <div className="flex items-center gap-1">
                <span className="material-symbols-outlined text-emerald-500 font-black">{t('mediaPowder.icons.biotech')}</span>
                <h3 className="text-xs font-black text-slate-800 uppercase tracking-widest">{t('mediaPowder.lotDetails.gpt.title')}</h3>
              </div>
              <span className="px-3 py-1 bg-emerald-50 text-emerald-600 text-[9px] font-black uppercase tracking-widest rounded-full border border-emerald-100">
                Passed USP &lt;61/62&gt;
              </span>
            </div>
            <div className="p-3">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                <div className="p-3 bg-slate-50 rounded-xl border border-slate-100">
                  <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1">{t('mediaPowder.lotDetails.gpt.reportId')}</p>
                  <p className="text-sm font-black text-slate-800">{gptTest?.report_id || "-"}</p>
                </div>
                <div className="p-3 bg-slate-50 rounded-xl border border-slate-100">
                  <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1">{t('mediaPowder.lotDetails.gpt.approvalDate')}</p>
                  <p className="text-sm font-black text-slate-800">
                    {gptTest?.approved_on?.split(" ")[0] || "-"}
                  </p>
                </div>
                <div className="p-3 bg-slate-50 rounded-xl border border-slate-100">
                  <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1">{t('mediaPowder.lotDetails.gpt.recoveryRate')}</p>
                  <p className="text-sm font-black text-emerald-600">{gptTest?.recovery_rate || "-"}</p>
                </div>
              </div>
              
              <div className="overflow-x-auto">
                <table className="w-full text-left">
                  <thead>
                    <tr className="text-[9px] font-black text-slate-400 uppercase tracking-widest border-b border-slate-100">
                      <th className="pb-4">{t('mediaPowder.lotDetails.gpt.testOrganism')}</th>
                      <th className="pb-4">{t('mediaPowder.lotDetails.gpt.atcc')}</th>
                      <th className="pb-4 text-center">{t('mediaPowder.lotDetails.gpt.inoculum')}</th>
                      <th className="pb-4 text-center">{t('mediaPowder.lotDetails.gpt.recovery')}</th>
                      <th className="pb-4 text-right">{t('mediaPowder.lotDetails.gpt.result')}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-50">
                    {gptTest?.organisms?.length > 0 ? (
                    gptTest.organisms.map((org: any, i: number) => (
                        <tr key={i} className="text-xs">
                          <td className="py-3 font-bold text-slate-700 italic">{org.organism_name || "-"}</td>
                          <td className="py-3 font-mono text-slate-400">{"-"}</td>
                          <td className="py-3 text-center font-black text-slate-500">{"-"}</td>
                          <td className="py-3 text-center font-black text-slate-800">{org.cfu_count || "-"}</td>
                          <td className="py-3 text-right">
                            <span
                              className={`text-[9px] font-black uppercase px-2 py-0.5 rounded ${
                                org.growth_observed === "t"
                                  ? "text-red-600 bg-red-50"
                                  : "text-emerald-600 bg-emerald-50"
                              }`}
                            >
                              {org.growth_observed === "t" ? "Fail" : "Pass"}
                            </span>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={5} className="text-center py-4 text-slate-400">
                          {t('mediaPowder.lotDetails.gpt.emptyTableText')}
                        </td>
                      </tr>
                    )}

                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-8 flex flex-col justify-between">
            <div>
              <h4 className="text-[10px] font-black uppercase tracking-widest mb-6 text-slate-400">{t('mediaPowder.lotDetails.gpt.summary')}</h4>
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-bold text-slate-600">{t('mediaPowder.lotDetails.gpt.conductedDate')}</span>
                  <span className="text-xs font-black text-slate-800">{gptTest?.gpt_test_date || "-"}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs font-bold text-slate-600">{t('mediaPowder.lotDetails.gpt.gptStatus')}</span>
                  <span className="text-xs font-black text-emerald-600 uppercase">{gptTest?.qa_status || "-"}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs font-bold text-slate-600">{t('mediaPowder.lotDetails.gpt.approvedBy')}</span>
                  <span className="text-xs font-black text-slate-800 uppercase">{gptTest?.approved_by || "-"}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs font-bold text-slate-600">{t('mediaPowder.lotDetails.gpt.approvedDate')}</span>
                  <span className="text-xs font-black text-slate-800 uppercase">{gptTest?.approved_on?.split(" ")[0] || "-"}</span>
                </div>
              </div>
            </div>
            
            <div className="mt-8 pt-6 border-t border-slate-100">
              {/* <button className="w-full py-3 bg-emerald-50 text-emerald-700 rounded-xl font-black text-[10px] uppercase tracking-widest hover:bg-emerald-100 transition-all flex items-center justify-center gap-2">
                <span className="material-symbols-outlined text-sm">{t('mediaPowder.icons.download')}</span>
                {t('mediaPowder.lotDetails.gpt.downloadCoa')}
              </button> */}
            </div>
          </div>
        </div>
  );
};

export default GptAuditSection;